// Data-layer hook: bridges the repository (cloud/local) and the Jotai in-memory
// cache, and centralizes error handling so pages stay simple.

import { useCallback } from 'react';
import { atom, useAtom } from 'jotai';
import { atomsAtom, eventBankAtom, eventsAtom, sourcesAtom, topicsAtom } from '@/lib/store';
import { repo, type EnrichEventInput, type IconikPublishResult, type ProduceOptions, type ProduceResult } from '@/lib/repo';
import { heuristicAtomizer, runAtomization } from '@/lib/atomize';
import type { NewsAtom, ReviewStatus, StoryEvent, Topic } from '@/lib/types';

export const lcsErrorAtom = atom<string | null>(null);

export function useLcs() {
  const [topics, setTopics] = useAtom(topicsAtom);
  const [sources, setSources] = useAtom(sourcesAtom);
  const [atoms, setAtoms] = useAtom(atomsAtom);
  const [events, setEvents] = useAtom(eventsAtom);
  const [eventBank, setEventBank] = useAtom(eventBankAtom);
  const [error, setError] = useAtom(lcsErrorAtom);

  const wrap = useCallback(
    async <T>(fn: () => Promise<T>): Promise<T | undefined> => {
      try {
        setError(null);
        return await fn();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Something went wrong');
        return undefined;
      }
    },
    [setError],
  );

  // Poll a background job to completion. Long operations (LLM atomize, event
  // clustering, Iconik publish, Story-Engine delegation) run as jobs so no request
  // hits Firebase Hosting's ~60s rewrite cap. Returns the job's result, or throws
  // its error message.
  const awaitJob = useCallback(async (jobId: string): Promise<unknown> => {
    const deadline = Date.now() + 15 * 60 * 1000; // generous — real render can run minutes
    for (;;) {
      const job = await repo.getJob(jobId);
      if (job.status === 'succeeded') return job.result;
      if (job.status === 'failed') throw new Error(job.error || 'The job failed');
      if (Date.now() > deadline) throw new Error('Timed out waiting for the job to finish');
      await new Promise((r) => setTimeout(r, 2500));
    }
  }, []);

  const refreshTopics = useCallback(
    () =>
      wrap(async () => {
        setTopics(await repo.listTopics());
      }),
    [wrap, setTopics],
  );

  const loadBundle = useCallback(
    (topicId: string) =>
      wrap(async () => {
        const b = await repo.getBundle(topicId);
        setTopics((p) => (p.some((t) => t.id === topicId) ? p.map((t) => (t.id === topicId ? b.topic : t)) : [b.topic, ...p]));
        setSources((p) => [...p.filter((s) => s.topicId !== topicId), ...b.sources]);
        setAtoms((p) => [...p.filter((a) => a.topic_id !== topicId), ...b.atoms]);
        setEvents((p) => [...p.filter((e) => e.topic_id !== topicId), ...(b.events ?? [])]);
      }),
    [wrap, setTopics, setSources, setAtoms, setEvents],
  );

  const renameTopic = useCallback(
    (topicId: string, title: string) =>
      wrap(async () => {
        await repo.renameTopic(topicId, title);
        setTopics((p) => p.map((t) => (t.id === topicId ? { ...t, title } : t)));
      }),
    [wrap, setTopics],
  );

  // Delete a topic and everything under it (sources, atoms, events), then prune
  // it from the in-memory caches.
  const deleteTopic = useCallback(
    (topicId: string) =>
      wrap(async () => {
        await repo.deleteTopic(topicId);
        setTopics((p) => p.filter((t) => t.id !== topicId));
        setSources((p) => p.filter((s) => s.topicId !== topicId));
        setAtoms((p) => p.filter((a) => a.topic_id !== topicId));
        setEvents((p) => p.filter((e) => e.topic_id !== topicId));
      }),
    [wrap, setTopics, setSources, setAtoms, setEvents],
  );

  // Delete a single source from a topic, then prune it from the in-memory cache
  // and decrement the topic's source count.
  const deleteSource = useCallback(
    (topicId: string, sourceId: string) =>
      wrap(async () => {
        await repo.deleteSource(topicId, sourceId);
        setSources((p) => p.filter((s) => !(s.topicId === topicId && s.id === sourceId)));
        setTopics((p) => p.map((t) => (t.id === topicId ? { ...t, sourceCount: Math.max(0, (t.sourceCount ?? 1) - 1) } : t)));
      }),
    [wrap, setSources, setTopics],
  );

  // Cluster the topic's atoms into events (EVENT tier) via a background job, then refresh.
  const clusterEvents = useCallback(
    (topicId: string) =>
      wrap(async () => {
        const { jobId } = await repo.clusterEvents(topicId);
        await awaitJob(jobId);
        const b = await repo.getBundle(topicId);
        setEvents((p) => [...p.filter((e) => e.topic_id !== topicId), ...(b.events ?? [])]);
        setAtoms((p) => [...p.filter((a) => a.topic_id !== topicId), ...b.atoms]);
      }),
    [wrap, setEvents, setAtoms, awaitJob],
  );

  // Persist the human-aggregated events (the manual aggregator), then reconcile
  // in-memory events + atom event_frames with the canonical saved set.
  const saveEventsFor = useCallback(
    (topicId: string, draft: StoryEvent[]) =>
      wrap(async () => {
        const { events } = await repo.saveEvents(topicId, draft);
        setEvents((p) => [...p.filter((e) => e.topic_id !== topicId), ...events]);
        const byAtom = new Map<string, StoryEvent>();
        for (const e of events) for (const id of e.atom_ids) byAtom.set(id, e);
        setAtoms((p) =>
          p.map((a) => {
            if (a.topic_id !== topicId) return a;
            const e = byAtom.get(a.atom_id);
            return { ...a, event_frame: e ? { event_id: e.event_id, event_label: e.label } : {} };
          }),
        );
        return events;
      }),
    [wrap, setEvents, setAtoms],
  );

  // AI-generate production-ready info for an assembled (possibly unsaved) event.
  const enrichEventDraft = useCallback(
    (topicId: string, input: EnrichEventInput) => wrap(() => repo.enrichEvent(topicId, input)),
    [wrap],
  );

  const saveAtomsFor = useCallback(
    (topicId: string, next: NewsAtom[]) =>
      wrap(async () => {
        await repo.saveAtoms(topicId, next);
        setAtoms((p) => [...p.filter((a) => a.topic_id !== topicId), ...next]);
        setTopics((p) => p.map((t) => (t.id === topicId ? { ...t, atomCount: next.length } : t)));
      }),
    [wrap, setAtoms, setTopics],
  );

  const updateAtomStatus = useCallback(
    (atomId: string, status: ReviewStatus) =>
      wrap(async () => {
        setAtoms((p) => p.map((a) => (a.atom_id === atomId ? { ...a, review_process: { ...a.review_process, status } } : a)));
        await repo.setAtomStatus(atomId, status);
      }),
    [wrap, setAtoms],
  );

  // Approve every candidate atom in the topic in one write.
  const approveAll = useCallback(
    (topicId: string) =>
      wrap(async () => {
        const next = atoms
          .filter((a) => a.topic_id === topicId)
          .map((a) =>
            a.review_process.status === 'candidate'
              ? { ...a, review_process: { ...a.review_process, status: 'approved' as const } }
              : a,
          );
        await repo.saveAtoms(topicId, next);
        setAtoms((p) => [...p.filter((a) => a.topic_id !== topicId), ...next]);
      }),
    [wrap, atoms, setAtoms],
  );

  // Run the atomizer for a topic. Heuristic runs client-side and saves directly;
  // llm runs as a background job (extract → cluster → persist server-side), then we
  // pull the saved atoms into state.
  const atomizeTopic = useCallback(
    (topicId: string, engineKey: string) =>
      wrap(async () => {
        if (engineKey === 'llm') {
          const { jobId } = await repo.startAtomize(topicId);
          await awaitJob(jobId);
          const b = await repo.getBundle(topicId);
          setAtoms((p) => [...p.filter((a) => a.topic_id !== topicId), ...b.atoms]);
          setTopics((p) => p.map((t) => (t.id === topicId ? b.topic : t)));
          return;
        }
        const topicSources = sources.filter((s) => s.topicId === topicId);
        const next = runAtomization(topicSources, heuristicAtomizer);
        await repo.saveAtoms(topicId, next);
        setAtoms((p) => [...p.filter((a) => a.topic_id !== topicId), ...next]);
        setTopics((p) => p.map((t) => (t.id === topicId ? { ...t, atomCount: next.length } : t)));
      }),
    [wrap, sources, setAtoms, setTopics, awaitJob],
  );

  // Publish the topic's atoms to Iconik (background job) so the Story Engine can pull them.
  const publishToIconik = useCallback(
    (topicId: string) =>
      wrap(async () => {
        const { jobId } = await repo.publishToIconik(topicId);
        return (await awaitJob(jobId)) as IconikPublishResult;
      }),
    [wrap, awaitJob],
  );

  // Send the topic to the Story Engine (background job) — delegates a footage production over MCP.
  const produce = useCallback(
    (topicId: string, opts?: ProduceOptions) =>
      wrap(async () => {
        const { jobId } = await repo.produce(topicId, opts);
        return (await awaitJob(jobId)) as ProduceResult;
      }),
    [wrap, awaitJob],
  );

  // Produce a CNN vertical from a picked EVENT (scoped to its atoms' clips).
  const produceEvent = useCallback(
    (topicId: string, eventId: string, opts?: ProduceOptions) =>
      wrap(async () => {
        const { jobId } = await repo.produceEvent(topicId, eventId, opts);
        return (await awaitJob(jobId)) as ProduceResult;
      }),
    [wrap, awaitJob],
  );

  // Story Engine genres/aspects for the produce picker.
  const loadCapabilities = useCallback(() => wrap(() => repo.getCapabilities()), [wrap]);

  // Cross-topic Event Bank (EVENT-BANK tier): load the persisted bank, or rebuild
  // it across all topics (Gemini dedups the same real-world event across topics).
  const loadEventBank = useCallback(
    () =>
      wrap(async () => {
        const { entries } = await repo.getEventBank();
        setEventBank(entries);
      }),
    [wrap, setEventBank],
  );

  const rebuildEventBank = useCallback(
    () =>
      wrap(async () => {
        const r = await repo.rebuildEventBank();
        setEventBank(r.entries);
        return r;
      }),
    [wrap, setEventBank],
  );

  // Drag-and-drop ingestion: upload each raw file to GCS, then analyze it into
  // clean text (Gemini for A/V/PDF/image). Returns the new topic.
  const createTopicAndIngest = useCallback(
    (title: string, files: File[], onProgress?: (done: number, total: number) => void) =>
      wrap(async (): Promise<Topic> => {
        const topic = await repo.createTopic({ title });
        setTopics((p) => [{ ...topic, sourceCount: 0 }, ...p.filter((t) => t.id !== topic.id)]);
        for (let i = 0; i < files.length; i++) {
          const f = files[i];
          const ct = f.type || 'application/octet-stream';
          const { sourceId, uploadUrl } = await repo.ingestUrl(topic.id, f.name, ct);
          const put = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': ct }, body: f });
          if (!put.ok) throw new Error(`Upload failed for ${f.name} (${put.status})`);
          const { jobId } = await repo.startAnalyze(topic.id, sourceId, ct);
          await awaitJob(jobId);
          onProgress?.(i + 1, files.length);
        }
        setTopics((p) => p.map((t) => (t.id === topic.id ? { ...t, sourceCount: files.length } : t)));
        // Auto-name the topic from what was actually ingested.
        const suggestion = await repo.suggestTitle(topic.id).catch(() => ({ title: '' }));
        if (suggestion.title) {
          setTopics((p) => p.map((t) => (t.id === topic.id ? { ...t, title: suggestion.title } : t)));
        }
        return { ...topic, title: suggestion.title || topic.title };
      }),
    [wrap, setTopics, awaitJob],
  );

  // Add more raw files to an EXISTING topic (upload + analyze), then refresh.
  const ingestIntoTopic = useCallback(
    (topicId: string, files: File[], onProgress?: (done: number, total: number) => void) =>
      wrap(async () => {
        for (let i = 0; i < files.length; i++) {
          const f = files[i];
          const ct = f.type || 'application/octet-stream';
          const { sourceId, uploadUrl } = await repo.ingestUrl(topicId, f.name, ct);
          const put = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': ct }, body: f });
          if (!put.ok) throw new Error(`Upload failed for ${f.name} (${put.status})`);
          const { jobId } = await repo.startAnalyze(topicId, sourceId, ct);
          await awaitJob(jobId);
          onProgress?.(i + 1, files.length);
        }
        const b = await repo.getBundle(topicId);
        setSources((p) => [...p.filter((s) => s.topicId !== topicId), ...b.sources]);
        setTopics((p) => p.map((t) => (t.id === topicId ? b.topic : t)));
      }),
    [wrap, setSources, setTopics, awaitJob],
  );

  return {
    topics,
    sources,
    atoms,
    events,
    eventBank,
    error,
    clusterEvents,
    saveEventsFor,
    enrichEventDraft,
    loadEventBank,
    rebuildEventBank,
    renameTopic,
    deleteTopic,
    deleteSource,
    refreshTopics,
    loadBundle,
    saveAtomsFor,
    updateAtomStatus,
    approveAll,
    atomizeTopic,
    publishToIconik,
    produce,
    produceEvent,
    loadCapabilities,
    createTopicAndIngest,
    ingestIntoTopic,
  };
}
